import React, { FC, useEffect, useState, useCallback, useRef } from "react";

import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

import moment from "moment";

Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVjJx26TKEr37c9WBI.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVmJx26TKEr37c9YL5rulw.ttf",
});
// Type definition for PDFDownloadLink render function
interface PDFDownloadLinkRenderProps {
  blob: Blob | null;
  url: string | null;
  loading: boolean;
  error: Error | null;
}

// ใช้ต่อท้ายข้อความไทยใน PDF เพื่อไม่ให้ตัวสุดท้ายถูกตัด (bug ของ @react-pdf/renderer)
const zw = "\u200B";

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const ReportEmployeeCountByGenderListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "เพศ",
      field: "gender",
      order: null,
      isOrder: true,
    },
    {
      label: `พนักงาน\nจำนวน (คน)`,
      field: "employee",
      order: null,
      isOrder: true,
    },
    {
      label: `ลูกจ้างประจำ\nจำนวน (คน)`,
      field: "permanent_employee",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result = await HrOnlineService.getReportEmployeeCountByGenderList(
          params
        );
        setData(result);
        setDataExport(result);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering_field, queryParams.ordering]
  );

  const tableRowNoData = () => {
    if (data.rows?.employees.length === 0) {
      return <TableRowNoData colspan={7} />;
    }
    return null;
  };

  const PDF: React.FC<{ data: any }> = ({ data }) => (
    <Document>
      <Page size="A4" style={{ padding: 10 }}>
        <View>
          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 16,
              textAlign: "center",
              marginBottom: 1,
            }}
          >
            {"องค์การตลาดเพื่อเกษตรกร" + zw}
          </Text>

          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 16,
              textAlign: "center",
              width: "100%",
              marginBottom: 1,
            }}
          >
            {"รายงานแบ่งประเภท : เพศ" + zw}
          </Text>

          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 16,
              textAlign: "center",
              marginBottom: 1,
            }}
          >
            {"ประจำวันที่" + zw}{" "}
            {moment().format("D") +
              " " +
              thaiMonths[moment().month()] +
              " " +
              (moment().year() + 543) +
              zw}
          </Text>

          {/* ตาราง - ครอบทั้งตารางด้วย View เดียว */}
          <View
            style={{
              border: "1px solid black",
            }}
          >
            {/* หัวตาราง */}
            <View
              style={{
                flexDirection: "row",
                borderBottom: "1px solid black",
              }}
              fixed
            >
              <Text
                style={{
                  width: "33.33%",
                  fontSize: 11,
                  fontFamily: "kanit-regular",
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {"เพศ" + zw}
              </Text>
              <Text
                style={{
                  width: "33.33%",
                  fontSize: 11,
                  fontFamily: "kanit-regular",
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {"พนักงาน" + zw}{"\n"}{"จำนวน (คน)" + zw}
              </Text>
              <Text
                style={{
                  width: "33.34%",
                  fontSize: 11,
                  fontFamily: "kanit-regular",
                  textAlign: "center",
                  padding: 5,
                }}
              >
                {"ลูกจ้างประจำ" + zw}{"\n"}{"จำนวน (คน)" + zw}
              </Text>
            </View>

            {/* ข้อมูลพนักงาน */}
            {data.rows?.employees.map((item: any, index: any) => (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  borderBottom:"1px solid black"
                }}
              >
                <Text
                  style={{
                    width: "33.33%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                    fontFamily: "kanit-200",
                  }}
                >
                  {item.gender === "MALE" ? "ชาย" : "หญิง" + zw}
                </Text>
                <Text
                  style={{
                    width: "33.33%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                    fontFamily: "kanit-200",
                  }}
                >
                  {item.personnel}
                </Text>
                <Text
                  style={{
                    width: "33.34%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontFamily: "kanit-200",
                  }}
                >
                  {item.permanent}
                </Text>
              </View>
            ))}

            {/* รวมทั้งหมด */}
            <View
              style={{
                flexDirection: "row",
                fontFamily: "kanit-regular",
                fontSize: 10,
                borderBottom: "1px solid black",
              }}
            >
              <Text
                style={{
                  width: "33.33%",
                  textAlign: "center",
                  padding: 5,
                  fontWeight: "bold",
                  borderRight: "1px solid black",
                }}
              >
                {"รวม" + zw}
              </Text>
              <Text
                style={{
                  width: "33.33%",
                  textAlign: "center",
                  padding: 5,
                  fontWeight: "bold",
                  borderRight: "1px solid black",
                }}
              >
                {data.rows?.total_summary?.personnel}
              </Text>
              <Text
                style={{
                  width: "33.34%",
                  textAlign: "center",
                  padding: 5,
                  fontWeight: "bold",
                }}
              >
                {data.rows?.total_summary?.permanent}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">รายงานแบ่งประเภท : เพศ</h2>
      </div>
      {loaderComponent}
      <div className="row">
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานแบ่งประเภท : เพศ_${moment().format("DDMMYYYY")}.pdf`}
          >
            {
              (({ loading }: any) => (
                <Button
                  variant="primary"
                  className="btn-action btn-mof-primary me-2"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  {loading ? "Loading..." : "Export"}
                </Button>
              )) as any
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data?.rows?.employees && Array.isArray(data.rows.employees)
              ? data.rows.employees.map((data: any, index: any) => {
                  return (
                    <ReportEmployeeCountByGenderRow key={index} {...data} />
                  );
                })
              : null}

            <tr style={{ fontWeight: "bold" }}>
              <td>รวม</td>
              <td>{data?.rows?.total_summary?.personnel || 0}</td>
              <td>{data?.rows?.total_summary?.permanent || 0}</td>
            </tr>
            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const ReportEmployeeCountByGenderRow: FC<any> = ({
  gender,
  permanent,
  personnel,
}) => {
  return (
    <tr>
      <td>{gender === "MALE" ? "ชาย" : "หญิง"}</td>
      <td>{personnel}</td>
      <td>{permanent}</td>
    </tr>
  );
};

export default ReportEmployeeCountByGenderListPage;
